import { FormItemType } from '@faceup/utils'
import { type FragmentType, getFragmentData, graphql } from '../__generated__'
import type { AFForm_itemFragment } from '../__generated__/graphql'
import type { AFFormItem, AFFormItemOption } from './types'

/**
 * The GraphQL adapter of AFForm. The component itself is GraphQL-free and renders plain
 * {@link AFFormItem} values — this file is the only place that knows about the `AFForm_item`
 * fragment shape. Non-GraphQL callers construct {@link AFFormItem} values directly.
 */
const fragments = {
  AFForm_item: graphql(`
    fragment AFForm_item on AF_FormItemInterface {
      id
      order
      isRequired
      type
      labelTranslation(language: $language) {
        translation
      }
      hintTranslation(language: $language) {
        translation
      }
      ... on AF_FormItemMultilineText {
        maxLength
      }
      ... on AF_FormItemWithOptionsInterface {
        options {
          id
          translation(language: $language) {
            id
            translation
            language
          }
        }
      }
      ... on AF_FormItemWithOptionsWithOtherInterface {
        hasOtherOption
      }
      ... on AF_FormItemMultiSelect {
        maxResponses
        minResponses
      }
      ... on AF_FormItemMatrix {
        matrixOptions: options {
          id
          order
        }
        questions {
          id
          order
        }
      }
      ... on AF_FormItemScale {
        startLabelTranslation(language: $language) {
          translation
        }
        endLabelTranslation(language: $language) {
          translation
        }
      }
    }
  `),
}

const mapOptions = (
  options: { id: string; translation: { translation: string } }[]
): AFFormItemOption[] =>
  options.map(option => ({ value: option.id, label: option.translation.translation }))

const mapAFFormItem = (item: AFForm_itemFragment): AFFormItem => {
  const base = {
    id: item.id,
    order: item.order,
    isRequired: item.isRequired,
    label: item.labelTranslation.translation,
    hint: item.hintTranslation?.translation ?? null,
  }

  switch (item.__typename) {
    case 'AF_FormItemMultilineText':
      return { ...base, type: FormItemType.MultilineText, maxLength: item.maxLength ?? null }
    case 'AF_FormItemSelect':
      return {
        ...base,
        type: FormItemType.Select,
        hasOtherOption: item.hasOtherOption,
        options: mapOptions(item.options),
      }
    case 'AF_FormItemMultiSelect':
      return {
        ...base,
        type: FormItemType.MultiSelect,
        hasOtherOption: item.hasOtherOption,
        options: mapOptions(item.options),
        minResponses: item.minResponses ?? null,
        maxResponses: item.maxResponses ?? null,
      }
    case 'AF_FormItemCategory':
      return { ...base, type: FormItemType.Category, options: mapOptions(item.options) }
    case 'AF_FormItemOrganizationalUnit':
      return { ...base, type: FormItemType.OrganizationalUnit, options: mapOptions(item.options) }
    case 'AF_FormItemRating':
      return { ...base, type: FormItemType.Rating, options: mapOptions(item.options) }
    case 'AF_FormItemScale':
      return {
        ...base,
        type: FormItemType.Scale,
        options: mapOptions(item.options),
        startLabel: item.startLabelTranslation?.translation ?? null,
        endLabel: item.endLabelTranslation?.translation ?? null,
      }
    case 'AF_FormItemMatrix':
      return {
        ...base,
        type: FormItemType.Matrix,
        questions: item.questions.map(question => ({ id: question.id })),
      }
    default:
      // The remaining GraphQL variants carry no type-specific fields; `type` is typed as the
      // loose FormItemType scalar in generated fragments, so this is the one boundary cast.
      return {
        ...base,
        type: item.type as
          | FormItemType.SimpleText
          | FormItemType.SenderName
          | FormItemType.Classroom
          | FormItemType.MoreInformation
          | FormItemType.Email
          | FormItemType.PhoneNumber
          | FormItemType.Number
          | FormItemType.Date
          | FormItemType.File,
      }
  }
}

/**
 * Converts masked `AFForm_item` fragment tokens to the plain {@link AFFormItem} contract.
 */
export const mapAFFormGraphQLItems = (
  items: FragmentType<typeof fragments.AFForm_item>[]
): AFFormItem[] => items.map(item => mapAFFormItem(getFragmentData(fragments.AFForm_item, item)))
